// Video + Audio (MP4) remux pipeline — port of legacy
// `exportTimelineVideo()` + helpers (legacy/index.html lines 8400–8910).
//
// The pipeline:
//   1. Decide between streamed (FileSystemWritableFileStream) output and
//      in-memory ArrayBufferTarget based on input size + feature detection.
//   2. Demux input MP4 via MP4Box.js: fetch body as a reader, feed
//      `appendBuffer({fileStart})` chunks, collect video samples + codec
//      config, release MP4Box's internal buffer after each batch.
//   3. Render the Mixed timeline audio (voice + video audio + ducking) via
//      `renderMixedAudioBuffer` from audioExport.ts. That render path
//      includes the brick-wall limiter on its master bus (#173 Phase 8),
//      so the AudioBuffer fed into the AAC encoder below is already
//      limited — video-MP4 audio = Mixed-bus + limiter (MP4 has no stems).
//   4. Encode that AudioBuffer to AAC via WebCodecs AudioEncoder.
//   5. Push chunks into mp4-muxer: ALL audio chunks first, THEN video chunks
//      (reverse order silently drops audio on mp4-muxer 5.2.1). Video uses
//      `addVideoChunkRaw(data, type, CTS, duration, meta?, CTO)` so B-frames
//      present in the correct order without dropping the AAC track.
//   6. Finalize muxer, close stream / download blob.
//
// Feature gate (`hasVideoExportSupport`) checks AudioEncoder + MP4Box +
// Mp4Muxer availability — Firefox hides the button as a result.
//
// mp4-muxer API quirks (per docs/patterns-timeline.md):
//   - `addVideoChunkRaw(data, type, timestamp=CTS, duration, meta?, CTO)` —
//     3rd arg is presentation timestamp, 6th is compositionTimeOffset so the
//     muxer derives DTS = CTS - CTO internally.
//   - `addAudioChunk(chunk)` WITHOUT `meta.decoderConfig` — mp4-muxer auto-
//     generates the AAC AudioSpecificConfig from muxer options.
//   - Audio chunks BEFORE video chunks.
//   - `DataStream` is a top-level global (NOT nested under MP4Box).

import type { ExportInput } from './audioExport';
import { renderMixedAudioBuffer } from './audioExport';
import { invalidatePreviewAudio } from './audioContext';
import { fetchVideo } from './videoSource';

// ─── Feature detection ──────────────────────────────────────────────────────

/** Synchronously determines whether the browser can run the MP4 remux
 *  pipeline. Must be called after both vendor scripts have loaded — index.html
 *  includes them synchronously in <body>, so by the time React renders the
 *  ExportBar this evaluates correctly. */
export function hasVideoExportSupport(): boolean {
  try {
    const hasEncoder = typeof window.AudioEncoder === 'function';
    const hasMP4Box = !!window.MP4Box && typeof window.MP4Box.createFile === 'function';
    const hasMuxer =
      !!window.Mp4Muxer && typeof window.Mp4Muxer.Muxer === 'function';
    return hasEncoder && hasMP4Box && hasMuxer;
  } catch {
    return false;
  }
}

// ─── Status reporter ────────────────────────────────────────────────────────

type StatusReporter = (status: string) => void;

// ─── AAC encoder ────────────────────────────────────────────────────────────

interface EncodedAacResult {
  chunks: EncodedAudioChunk[];
}

/** Feed the rendered AudioBuffer into a WebCodecs AudioEncoder and collect
 *  every emitted EncodedAudioChunk. Frame size = 1024 (standard AAC). The
 *  emitted `decoderConfig` is intentionally discarded — mp4-muxer generates
 *  its own AudioSpecificConfig from `muxer.audio.sampleRate/numberOfChannels`
 *  and merging ours in corrupts the track. */
async function encodeMixedAudioToAacChunks(
  mixedBuffer: AudioBuffer,
  signal?: AbortSignal,
): Promise<EncodedAacResult> {
  const sampleRate = mixedBuffer.sampleRate;
  const numCh = mixedBuffer.numberOfChannels;
  const chunks: EncodedAudioChunk[] = [];

  await new Promise<void>((resolve, reject) => {
    const encoder = new AudioEncoder({
      output: (chunk) => {
        chunks.push(chunk);
      },
      error: (e) => reject(e),
    });
    encoder.configure({
      codec: 'mp4a.40.2',     // AAC-LC
      sampleRate,
      numberOfChannels: numCh,
      bitrate: 192_000,
    });

    // If a cancel was already requested while we were waiting for the
    // mixedBuffer render, bail before doing any encode work.
    if (signal?.aborted) {
      try { encoder.close(); } catch { /* ignore */ }
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
      return;
    }
    const onAbort = () => {
      try { encoder.close(); } catch { /* ignore */ }
      reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });

    const total = mixedBuffer.length;
    const chans: Float32Array[] = [];
    for (let c = 0; c < numCh; c++) chans.push(mixedBuffer.getChannelData(c));

    const FRAME = 1024;
    const sampleDurationUs = 1_000_000 / sampleRate;
    let processed = 0;
    while (processed < total) {
      // Cheap inline check — encode loop is synchronous, polling per
      // FRAME means worst-case ~21ms before bailing.
      if (signal?.aborted) {
        try { encoder.close(); } catch { /* ignore */ }
        reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
        return;
      }
      const len = Math.min(FRAME, total - processed);
      const data = new Float32Array(len * numCh);
      for (let i = 0; i < len; i++) {
        for (let c = 0; c < numCh; c++) {
          data[i * numCh + c] = chans[c][processed + i];
        }
      }
      const ad = new AudioData({
        format: 'f32',
        sampleRate,
        numberOfFrames: len,
        numberOfChannels: numCh,
        timestamp: Math.round(processed * sampleDurationUs),
        data,
      });
      try {
        encoder.encode(ad);
      } catch (e) {
        ad.close();
        reject(e);
        return;
      }
      ad.close();
      processed += len;
    }

    encoder
      .flush()
      .then(() => {
        encoder.close();
        signal?.removeEventListener('abort', onAbort);
        resolve();
      })
      .catch(reject);
  });

  // Diagnose für den AAC-Priming-Versatz: mp4-muxer 5.2.1 schreibt keine
  // Edit-List (edts/elst), die Encoder-Vorlaufsamples (typisch 1024–2112 ≈
  // 21–44 ms) spielen deshalb vor dem eigentlichen Ton. Ob Chrome sie als
  // negativen Zeitstempel meldet oder im ersten Chunk versteckt, zeigt diese
  // Zeile — Grundlage für eine spätere Korrektur (decisions.md 2026-09-24).
  if (chunks.length > 0) {
    console.info(
      `[videoExport] AAC first chunk: timestamp=${chunks[0].timestamp}µs duration=${chunks[0].duration ?? '?'}µs, chunks=${chunks.length}, pcm=${Math.round((mixedBuffer.length / sampleRate) * 1e6)}µs`,
    );
  }
  return { chunks };
}

// ─── Freeze-frame padding (overlength voice → MP4) ──────────────────────────
//
// When a voice clip extends past the input video's duration the muxed audio
// track is longer than the video track. Most players freeze the last frame
// during the audio tail anyway — but QuickTime + ffmpeg flag the mismatched
// track durations as a broken file. The fix is container-only: the mux
// duration of the PRESENTATION-last sample is extended so the video track's
// declared duration covers the audio (mp4-muxer 5.2.1 derives tkhd/mdhd/mvhd
// durations from max(presentationTimestamp + duration); stts is built from
// DTS deltas, so a longer final duration adds no gaps mid-stream). No new
// frames are encoded, no bytes are appended — players show the final frame
// frozen for the tail, exactly the behavior they already exhibit for short
// overhangs.
//
// History: this used to append WebCodecs-encoded black frames. That was
// fundamentally broken — mp4-muxer writes exactly ONE avcC/hvcC (SPS/PPS)
// per track (from the source decoderConfig at sample 0), so slices from a
// second encoder referenced parameter sets that never landed in the file →
// garbage frames / decode errors on the padded tail (audit 2026-07-17).
// Decision: docs/decisions.md 2026-07-23.

export interface FreezePadResult {
  /** Index into `samples` of the presentation-last sample, or -1 for no-op. */
  index: number;
  /** Extra microseconds to add to that sample's mux duration. */
  extraUs: number;
}

/** Pure math: which demuxed sample's duration to extend, and by how much,
 *  so the video track covers the rendered audio. Presentation-last (max
 *  CTS+duration), not decode-last — with B-frames the final decode-order
 *  sample can present BEFORE an earlier P-frame, and extending that one
 *  would leave the track short. Covered by videoExport.test.ts. */
export function computeFreezePad(
  samples: ReadonlyArray<{ cts: number; duration: number }>,
  timescale: number,
  audioDurationSec: number,
): FreezePadResult {
  if (samples.length === 0 || !(timescale > 0)) return { index: -1, extraUs: 0 };
  let index = 0;
  let endTicks = samples[0].cts + samples[0].duration;
  for (let i = 1; i < samples.length; i++) {
    const end = samples[i].cts + samples[i].duration;
    if (end > endTicks) {
      endTicks = end;
      index = i;
    }
  }
  const extraUs = Math.round(
    audioDurationSec * 1_000_000 - (endTicks * 1_000_000) / timescale,
  );
  if (extraUs <= 0) return { index: -1, extraUs: 0 };
  return { index, extraUs };
}

// ─── Codec / decoder config from MP4Box ─────────────────────────────────────

interface DemuxResult {
  trackInfo: {
    id: number;
    codec: string;
    timescale: number;
    video: { width: number; height: number };
    /** Drehung der Quelle für mp4-muxer (undefined = keine). */
    rotation?: number[];
  };
  decoderConfig: VideoDecoderConfig;
  samples: Array<{
    data: Uint8Array;
    is_sync: boolean;
    dts: number;
    cts: number;
    duration: number;
  }>;
}

/** Byte-Stream der Video-Quelle. Ein vom Talent geladenes Video kommt als
 *  Blob und wird direkt gelesen — `fetch('blob:…')` blockt die Produktions-CSP
 *  (`connect-src 'self'`), der Mux fiel dadurch still auf das Rohvideo zurück
 *  (Befund 2026-09-21). Nur echte URLs (Admin-Video: same-origin Stream)
 *  gehen über fetch. */
export async function openVideoSource(
  src: string | Blob,
  signal?: AbortSignal,
  refreshMediaSession?: () => Promise<unknown>,
): Promise<ReadableStream<Uint8Array>> {
  if (typeof src !== 'string') return src.stream() as ReadableStream<Uint8Array>;
  // Status-Prüfung + einmaliger Media-Session-Refresh bei 401 (Audit M18).
  const res = await fetchVideo(src, { signal, refreshMediaSession });
  if (!res.body) {
    throw new Error('Response has no body');
  }
  return res.body;
}

/** tkhd-Matrix der Quelle (MP4Box: Int32, a/b/c/d/x/y in 16.16, u/v/w in
 *  2.30) → Gleitkomma-Matrix für mp4-muxer (`video.rotation`, gleiche
 *  Reihenfolge a,b,u,c,d,v,x,y,w). Identität oder unbrauchbare Werte →
 *  undefined, dann schreibt mp4-muxer seine Standardmatrix. Die volle Matrix
 *  statt eines Winkels, damit auch die Verschiebung (x/y) der Quelle erhalten
 *  bleibt. */
export function muxerMatrixFromTkhd(m: ArrayLike<number> | undefined): number[] | undefined {
  if (!m || m.length !== 9) return undefined;
  const F16 = 65536;
  const F30 = 1 << 30;
  const out = [
    m[0] / F16, m[1] / F16, m[2] / F30,
    m[3] / F16, m[4] / F16, m[5] / F30,
    m[6] / F16, m[7] / F16, m[8] / F30,
  ];
  if (out.some((v) => !Number.isFinite(v))) return undefined;
  const identity = [1, 0, 0, 0, 1, 0, 0, 0, 1];
  if (out.every((v, i) => Math.abs(v - identity[i]) < 1e-9)) return undefined;
  // Nur Drehungen/Spiegelungen in 90°-Schritten übernehmen — alles andere
  // (Scherung, Skalierung) ist in echten Kameradateien nicht zu erwarten und
  // würde das Bild verzerren.
  const [a, b, , c, d] = out;
  const isQuarterTurn =
    [a, b, c, d].every((v) => Math.abs(v) < 1e-6 || Math.abs(Math.abs(v) - 1) < 1e-6) &&
    Math.abs(Math.abs(a * d - b * c) - 1) < 1e-6;
  return isQuarterTurn ? out : undefined;
}

/** ArrayBuffer für MP4Box aus einem Stream-Chunk. Deckt die View ihren
 *  Puffer vollständig ab (der Normalfall bei fetch-Body und Blob.stream()),
 *  wird er direkt übernommen — die Streams geben jeden Chunk frisch aus und
 *  benutzen ihn nicht wieder. Nur Teil-Views werden kopiert (Audit M20:
 *  vorher wurde jeder Chunk kopiert). */
export function toOwnedArrayBuffer(view: Uint8Array): ArrayBuffer {
  if (
    view.byteOffset === 0 &&
    view.byteLength === view.buffer.byteLength &&
    view.buffer instanceof ArrayBuffer
  ) {
    return view.buffer;
  }
  const copy = new ArrayBuffer(view.byteLength);
  new Uint8Array(copy).set(view);
  return copy;
}

async function demuxVideo(
  src: string | Blob,
  report: StatusReporter,
  signal?: AbortSignal,
  refreshMediaSession?: () => Promise<unknown>,
): Promise<DemuxResult> {
  const MP4Box = window.MP4Box;
  if (!MP4Box) throw new Error('MP4Box not loaded');
  const DS = (globalThis as { DataStream?: typeof DataStream }).DataStream;
  if (!DS) throw new Error('DataStream not loaded');

  const inFile = MP4Box.createFile();
  let demuxError: Error | null = null;
  let trackInfo: DemuxResult['trackInfo'] | null = null;
  let decoderConfig: VideoDecoderConfig | null = null;
  let totalSamples = 0;
  const samples: DemuxResult['samples'] = [];

  inFile.onError = (e) => {
    demuxError = new Error('MP4Box: ' + e);
  };

  inFile.onReady = (info) => {
    const vt = info.tracks.find((t) => t.type === 'video');
    if (!vt) {
      demuxError = new Error('No video track in container');
      return;
    }
    trackInfo = {
      id: vt.id,
      codec: vt.codec,
      timescale: vt.timescale,
      video: { width: vt.video.width, height: vt.video.height },
      rotation: muxerMatrixFromTkhd(vt.matrix),
    };
    totalSamples = vt.nb_samples || 0;

    const trak = inFile.moov.traks.find((t) => t.tkhd.track_id === vt.id);
    const sampleEntry = trak?.mdia?.minf?.stbl?.stsd?.entries?.[0];
    const codecBox = sampleEntry?.avcC ?? sampleEntry?.hvcC;
    if (!codecBox) {
      demuxError = new Error(
        'Unsupported video codec — only H.264/H.265 in MP4 supported'
      );
      return;
    }
    // Box payload minus the 8-byte ISOBMFF header → WebCodecs `description`.
    const ds = new DS(undefined, 0, DS.BIG_ENDIAN);
    codecBox.write(ds);
    const fullBox = new Uint8Array(ds.buffer);
    decoderConfig = {
      codec: vt.codec,
      description: fullBox.slice(8),
      codedWidth: vt.video.width,
      codedHeight: vt.video.height,
    };

    inFile.setExtractionOptions(vt.id, null, { nbSamples: 100 });
    // Auch alle anderen Spuren (Ton, Timecode …) extrahieren und sofort
    // wieder freigeben: MP4Box entfernt einen angehängten Puffer erst, wenn
    // ALLE seine Bytes als Sample gelesen wurden (`usedBytes === byteLength`,
    // cleanBuffers). Mit nur der Videospur blieb bei verschachtelten Dateien
    // jeder Puffer liegen — die komplette Datei stand zusätzlich zu den
    // Video-Samples im Heap (Audit M20).
    for (const t of info.tracks) {
      if (t.id !== vt.id) inFile.setExtractionOptions(t.id, null, { nbSamples: 100 });
    }
    inFile.start();
  };

  inFile.onSamples = (trackId, _user, ss) => {
    if (!trackInfo) return;
    if (trackId !== trackInfo.id) {
      // Nicht-Video-Spur: nur als „gelesen" markieren, damit MP4Box die
      // Puffer freigeben kann (siehe setExtractionOptions oben).
      const last = ss[ss.length - 1];
      if (last) inFile.releaseUsedSamples(trackId, last.number + 1);
      return;
    }
    let lastNum = 0;
    for (const s of ss) {
      samples.push({
        // MP4Box legt `s.data` pro Sample frisch an (getSample: new
        // Uint8Array(size) + memcpy) und lässt beim releaseUsedSamples nur
        // seine Referenz fallen — keine Kopie nötig. Die frühere
        // `new Uint8Array(s.data)`-Kopie verdoppelte die Video-Daten
        // kurzzeitig pro Batch (Audit M20).
        data: s.data,
        is_sync: s.is_sync,
        dts: s.dts,
        cts: s.cts,
        duration: s.duration,
      });
      lastNum = s.number;
    }
    inFile.releaseUsedSamples(trackInfo.id, lastNum);
    if (totalSamples) {
      const pct = Math.min(
        100,
        Math.round((100 * samples.length) / totalSamples)
      );
      report(`Reading video… ${pct}%`);
    }
  };

  signal?.throwIfAborted();
  const reader = (await openVideoSource(src, signal, refreshMediaSession)).getReader();
  let fileStart = 0;
  while (true) {
    signal?.throwIfAborted();
    const { done, value } = await reader.read();
    if (done) break;
    if (demuxError) throw demuxError;
    // MP4Box wants an ArrayBuffer with .fileStart set.
    const chunkAB = toOwnedArrayBuffer(value) as ArrayBuffer & {
      fileStart: number;
    };
    chunkAB.fileStart = fileStart;
    fileStart += chunkAB.byteLength;
    inFile.appendBuffer(chunkAB);
  }
  inFile.flush();
  if (demuxError) throw demuxError;
  if (!trackInfo) throw new Error('Container has no parseable moov');
  if (!decoderConfig) throw new Error('Could not derive decoder config');
  if (!samples.length) throw new Error('Could not extract video samples');

  return { trackInfo, decoderConfig, samples };
}

// ─── Main entry point ───────────────────────────────────────────────────────

export interface VideoExportInput {
  /** Video source: the Blob of a talent-loaded video, or the same-origin
   *  streaming /api URL of an admin-provided one. Never a blob: URL — the
   *  production CSP blocks fetching those (see `openVideoSource`). */
  videoSrc: string | Blob;
  /** Raw byte count of the input video, or 0 if unknown. Controls the
   *  in-memory vs. streaming-output decision. */
  inputSize: number;
  /** Suggested download filename (with .mp4 extension). */
  suggestedFilename: string;
  /** All inputs the Mixed audio render needs (voice clips + mixer state). */
  audio: ExportInput;
  /** Pre-rendered Mixed audio buffer. When set, the pipeline skips its own
   *  renderMixedAudioBuffer() call — the upload flow renders the PREMIX WAV
   *  from the same buffer moments earlier, and re-rendering here would fetch
   *  + decode the full reference video a SECOND time (OOM driver on >1GB
   *  videos, audit 2026-07-17). Download callers omit it. */
  premixedAudio?: AudioBuffer;
  /** Status reporter invoked at each pipeline stage. */
  onStatus?: StatusReporter;
  /** Aborts the mux pipeline at the next natural await boundary
   *  (between demux chunks, between encode samples, between mux iterations).
   *  Throws AbortError up to the caller; on-disk OPFS temp file is wiped
   *  inline before re-throwing. */
  signal?: AbortSignal;
}

/** Mixed-Audio für den Mux + die Quelle, aus der danach gedemuxt wird.
 *  Liegt schon ein Premix vor (Upload), bleibt alles wie übergeben. Sonst
 *  rendert der Mix selbst; ist das Video eine URL, gibt der Render die
 *  geladene Datei als Blob zurück — ein Download statt zwei (Audit M20). */
async function renderMixForMux(
  input: VideoExportInput,
  audioInput: ExportInput,
): Promise<{ mixedBuffer: AudioBuffer; videoSrc: string | Blob }> {
  if (input.premixedAudio) {
    return { mixedBuffer: input.premixedAudio, videoSrc: input.videoSrc };
  }
  let loaded: Blob | null = null;
  const sameUrl =
    typeof input.videoSrc === 'string' && audioInput.videoSrc === input.videoSrc;
  const mixedBuffer = await renderMixedAudioBuffer(
    sameUrl
      ? { ...audioInput, onVideoSourceLoaded: (b) => { loaded = b; } }
      : audioInput,
  );
  return { mixedBuffer, videoSrc: loaded ?? input.videoSrc };
}

/** Download-Export: direkt auf die Platte (Save-Dialog, StreamTarget) oder
 *  im Speicher (ArrayBufferTarget + <a download>)? Im Speicher nur, wenn die
 *  Größe bekannt und klein ist — bei unbekannter Größe (HEAD gescheitert)
 *  lieber einmal den Save-Dialog als ein In-Memory-Mux, der bei einem
 *  1-GB-Video am ~2-GB-ArrayBuffer-Deckel stirbt (Audit, Low „Export"). Ohne
 *  showSaveFilePicker bleibt nur der Speicher. */
export function chooseVideoOutput(
  inputSize: number,
  hasSaveFilePicker: boolean,
): 'stream' | 'memory' {
  if (!hasSaveFilePicker) return 'memory';
  if (!(inputSize > 0)) return 'stream';
  return inputSize > VIDEO_EXPORT_STREAM_THRESHOLD ? 'stream' : 'memory';
}

export class VideoExportCancelledError extends Error {
  constructor() {
    super('Video export cancelled');
    this.name = 'VideoExportCancelledError';
  }
}

import { VIDEO_EXPORT_STREAM_THRESHOLD } from '@shared/constants';

/** Run the full MP4 remux pipeline. Resolves when the output file is saved
 *  (either auto-download or user-picked FileSystemWritableFileStream closed).
 *  Throws `VideoExportCancelledError` if the user cancels the save-file
 *  picker; any other error is re-thrown for the caller to toast.
 *
 *  ALWAYS invalidates the preview AudioContext at completion — WebCodecs +
 *  MP4Box buffers leave Safari in a silently-dead state otherwise. */
export async function exportTimelineVideo(
  input: VideoExportInput
): Promise<void> {
  if (!hasVideoExportSupport()) {
    throw new Error('Video export not supported in this browser');
  }
  const MP4Box = window.MP4Box;
  const Mp4Muxer = window.Mp4Muxer;
  if (!MP4Box || !Mp4Muxer) {
    throw new Error('Vendor libraries not loaded');
  }

  const report = input.onStatus ?? (() => {});
  report('Preparing export…');

  const useStreamOutput =
    chooseVideoOutput(input.inputSize, typeof window.showSaveFilePicker === 'function') === 'stream';

  let writable: FileSystemWritableFileStream | null = null;

  try {
    if (useStreamOutput) {
      try {
        // Typing: narrowed by the `typeof === 'function'` in useStreamOutput
        // above, but TS needs the local const to forget the union.
        const picker = window.showSaveFilePicker!;
        const handle = await picker({
          suggestedName: input.suggestedFilename,
          types: [
            { description: 'MP4 video', accept: { 'video/mp4': ['.mp4'] } },
          ],
        });
        writable = await handle.createWritable();
      } catch (e) {
        if ((e as DOMException)?.name === 'AbortError') {
          throw new VideoExportCancelledError();
        }
        throw e;
      }
    }

    // ── 1. Render the mixed audio FIRST — it loads the video anyway (for
    // its sound) and hands the file over as a Blob, so the demux below
    // reads it from there instead of downloading it a second time
    // (Audit M20). Limiter: see audioExport.ts (Mixed master bus).
    report('Rendering audio…');
    const { mixedBuffer, videoSrc } = await renderMixForMux(input, input.audio);

    // ── 2. Demux the input video.
    report(useStreamOutput ? 'Streaming video to disk…' : 'Reading video…');
    const { trackInfo, decoderConfig, samples } = await demuxVideo(
      videoSrc,
      report,
      undefined,
      input.audio.refreshMediaSession,
    );

    report('Encoding AAC…');
    const aac = await encodeMixedAudioToAacChunks(mixedBuffer);

    // ── 3. Build muxer with the matching codec family.
    report('Muxing MP4…');
    const codecFamily: 'avc' | 'hevc' =
      trackInfo.codec.startsWith('hvc1') || trackInfo.codec.startsWith('hev1')
        ? 'hevc'
        : 'avc';

    const muxer = new Mp4Muxer.Muxer({
      target: writable
        ? new Mp4Muxer.StreamTarget({
            onData: (data, position) => {
              // writable.write is async but the muxer's onData is sync — we
              // fire-and-forget; any write error is surfaced on close().
              // Copy to a fresh ArrayBuffer so the FS-API accepts it (some
              // TS DOM-lib versions reject SharedArrayBuffer-typed views).
              const copy = new Uint8Array(data.byteLength);
              copy.set(data);
              writable
                ?.write({ type: 'write', position, data: copy })
                .catch((err) => {
                  console.error('[videoExport] writable.write failed', err);
                });
            },
          })
        : new Mp4Muxer.ArrayBufferTarget(),
      video: {
        codec: codecFamily,
        width: trackInfo.video.width,
        height: trackInfo.video.height,
        // Hochkant-Videos (Handy) tragen die Drehung nur in der tkhd-Matrix;
        // ohne sie lag das Premix-MP4 quer (Audit, Low „Export").
        ...(trackInfo.rotation ? { rotation: trackInfo.rotation } : {}),
      },
      audio: {
        codec: 'aac',
        numberOfChannels: mixedBuffer.numberOfChannels,
        sampleRate: mixedBuffer.sampleRate,
      },
      firstTimestampBehavior: 'offset',
      // moov at start for the in-memory blob path so browsers can seek
      // immediately; moov at end for the stream path (player seeks).
      fastStart: writable ? false : 'in-memory',
    });

    // Push audio chunks FIRST. Passing meta.decoderConfig here would merge
    // badly with mp4-muxer's internal AudioSpecificConfig and silently drop
    // the track on 5.2.1 — we let the muxer synthesize from the audio.*
    // options it got above.
    for (const chunk of aac.chunks) {
      muxer.addAudioChunk(chunk);
    }

    // Timescale conversion — MP4Box gives DTS/CTS in `trackInfo.timescale`
    // ticks, mp4-muxer wants microseconds.
    const tsScale = 1_000_000 / trackInfo.timescale;
    // Freeze-frame pad: extend the presentation-last sample so the video
    // track duration covers an overlength audio tail (see computeFreezePad).
    const freeze = computeFreezePad(
      samples, trackInfo.timescale, mixedBuffer.length / mixedBuffer.sampleRate,
    );
    for (let i = 0; i < samples.length; i++) {
      const s = samples[i];
      muxer.addVideoChunkRaw(
        s.data,
        s.is_sync ? 'key' : 'delta',
        Math.round(s.cts * tsScale),
        Math.round(s.duration * tsScale) +
          (i === freeze.index ? freeze.extraUs : 0),
        i === 0 ? { decoderConfig } : undefined,
        Math.round((s.cts - s.dts) * tsScale)
      );
    }

    report('Saving…');
    muxer.finalize();

    if (writable) {
      await writable.close();
      writable = null;
    } else {
      const target = muxer.target;
      if (!('buffer' in target)) throw new Error('Muxer target has no buffer');
      const blob = new Blob([target.buffer], { type: 'video/mp4' });
      // Download via a transient <a>. Revoke after the click.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = input.suggestedFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  } catch (err) {
    if (writable) {
      try {
        await writable.abort();
      } catch {
        /* ignore */
      }
    }
    throw err;
  } finally {
    // WebCodecs + MP4Box leave heavy buffers that wreck Safari's preview
    // AudioContext — rebuild on next play so audible playback recovers.
    invalidatePreviewAudio('after video export');
  }
}

/** Run the full MP4 remux pipeline and return the muxed Blob.
 *  Used by the upload orchestrator so the "Include video" upload ships the
 *  timeline-mixed MP4 (voice + video audio + SFX bed with ducking) rather
 *  than the raw input video.
 *
 *  Implementation: write the muxer output to a temp file in OPFS (Origin
 *  Private File System) via FileSystemSyncAccessHandle. The muxer's
 *  StreamTarget callbacks include a `position` argument because mp4-muxer
 *  patches the mdat box header AFTER writing the data (seek-back), so
 *  output is NOT pure-append. OPFS gives us random-access writes against a
 *  browser-managed disk-backed file with no per-buffer V8 size limit.
 *
 *  Earlier attempts:
 *  - `ArrayBufferTarget` + `fastStart: 'in-memory'` → buffered the whole
 *    output in a single ArrayBuffer, hit V8's ~2GB ceiling on >1GB videos
 *    with "Array buffer allocation failed".
 *  - `StreamTarget` + chunk-array + `fastStart: false` → looked clean but
 *    silently dropped the position info, producing corrupt MP4s where the
 *    mdat-header patches landed at the WRONG position. (User report
 *    2026-05-05: 1.88GB output couldn't be opened.)
 *
 *  Tradeoff: still uses `fastStart: false` (moov at end). The download
 *  path keeps `fastStart: 'in-memory'` because it can fit in RAM there
 *  (download is small-to-medium files; large files use the showSaveFilePicker
 *  StreamTarget path which is FS-backed in a different way).
 *
 *  ALWAYS invalidates the preview AudioContext at completion — same Safari
 *  recovery behaviour as `exportTimelineVideo`. */
export async function exportTimelineVideoToBlob(
  input: VideoExportInput
): Promise<Blob> {
  if (!hasVideoExportSupport()) {
    throw new Error('Video export not supported in this browser');
  }
  const MP4Box = window.MP4Box;
  const Mp4Muxer = window.Mp4Muxer;
  if (!MP4Box || !Mp4Muxer) {
    throw new Error('Vendor libraries not loaded');
  }

  const report = input.onStatus ?? (() => {});

  // OPFS setup — fail fast if the browser doesn't have it. (Chrome 102+,
  // Edge 102+, Safari 17+, Firefox 111+. Voice Studio recommends Chromium
  // browsers in the welcome hint — OPFS is universally available there.)
  if (!('storage' in navigator) || !navigator.storage?.getDirectory) {
    throw new Error('OPFS not supported in this browser — required for >1GB video upload mux');
  }

  // Unique temp filename in OPFS root. Cleaned up in the finally block.
  const tempName = `voicestudio-mux-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.mp4`;
  const opfsRoot = await navigator.storage.getDirectory();
  const fileHandle = await opfsRoot.getFileHandle(tempName, { create: true });
  // createWritable() returns a FileSystemWritableFileStream that accepts
  // `write({type:'write', position, data})` calls — same async pattern as
  // the showSaveFilePicker path. We use this instead of the sync API
  // (createSyncAccessHandle) because the sync version is only available
  // in Web Workers in some browsers (Edge in particular — confirmed
  // 2026-05-05 with `createSyncAccessHandle is not a function` TypeError).
  // The async API is universally available in main-thread since OPFS
  // launched.
  const writable = await fileHandle.createWritable({ keepExistingData: false });

  try {
    report('Rendering audio…');

    // Forward the upload-cancel signal into the audio sub-pipeline. Caller
    // passes the same signal here and via input.audio.signal; we set it
    // explicitly so callers don't have to remember to wire both fields.
    const audioInput = input.audio.signal === input.signal
      ? input.audio
      : { ...input.audio, signal: input.signal };

    input.signal?.throwIfAborted();
    // ── 1. Mixed audio first (reuses the video bytes for the demux, see
    // renderMixForMux). The upload flow passes `premixedAudio`, then this
    // is a no-op and the pipeline hands in the already-loaded Blob.
    const { mixedBuffer, videoSrc } = await renderMixForMux(input, audioInput);

    input.signal?.throwIfAborted();
    // ── 2. Demux the input video.
    const { trackInfo, decoderConfig, samples } = await demuxVideo(
      videoSrc,
      report,
      input.signal,
      input.audio.refreshMediaSession,
    );

    input.signal?.throwIfAborted();
    report('Encoding AAC…');
    const aac = await encodeMixedAudioToAacChunks(mixedBuffer, input.signal);

    input.signal?.throwIfAborted();
    // ── 3. Build muxer with the matching codec family.
    report('Muxing MP4…');
    const codecFamily: 'avc' | 'hevc' =
      trackInfo.codec.startsWith('hvc1') || trackInfo.codec.startsWith('hev1')
        ? 'hevc'
        : 'avc';

    // Capture any async write error from the fire-and-forget pattern below.
    // Surfaced via close() (which awaits pending writes) or after finalize.
    let writeError: Error | null = null;

    const muxer = new Mp4Muxer.Muxer({
      target: new Mp4Muxer.StreamTarget({
        onData: (data, position) => {
          if (writeError) return; // bail-out if a prior write already failed
          // mp4-muxer calls onData with `position` because it patches the
          // mdat box header AFTER writing the data (seek-back). Ignoring
          // position and pure-appending corrupts the file (silent — file
          // opens size-correct but won't play). Verified: must respect
          // position.
          //
          // Copy to fresh buffer so we own the bytes (mp4-muxer reuses
          // internal buffers across calls).
          const copy = new Uint8Array(data.byteLength);
          copy.set(data);
          // Fire-and-forget — writes are queued in order by the
          // FileSystemWritableFileStream contract. close() awaits all
          // pending writes before resolving, so we'll see all data
          // committed by the time we return.
          writable.write({ type: 'write', position, data: copy }).catch((err) => {
            if (!writeError) writeError = err instanceof Error ? err : new Error(String(err));
          });
        },
      }),
      video: {
        codec: codecFamily,
        width: trackInfo.video.width,
        height: trackInfo.video.height,
        // Hochkant-Videos (Handy) tragen die Drehung nur in der tkhd-Matrix;
        // ohne sie lag das Premix-MP4 quer (Audit, Low „Export").
        ...(trackInfo.rotation ? { rotation: trackInfo.rotation } : {}),
      },
      audio: {
        codec: 'aac',
        numberOfChannels: mixedBuffer.numberOfChannels,
        sampleRate: mixedBuffer.sampleRate,
      },
      firstTimestampBehavior: 'offset',
      // moov at end — required for streaming output. fastStart:'in-memory'
      // would buffer everything until finalize() and defeat OPFS streaming.
      fastStart: false,
    });

    // Audio chunks BEFORE video chunks (mp4-muxer 5.2.1 quirk).
    for (const chunk of aac.chunks) {
      muxer.addAudioChunk(chunk);
    }

    const tsScale = 1_000_000 / trackInfo.timescale;
    // Freeze-frame pad: extend the presentation-last sample so the video
    // track duration covers an overlength audio tail (see computeFreezePad).
    const freeze = computeFreezePad(
      samples, trackInfo.timescale, mixedBuffer.length / mixedBuffer.sampleRate,
    );
    // Mux loop is synchronous; check the abort signal every N samples so
    // the loop bails within a few ms of cancel even on long videos.
    const ABORT_CHECK_EVERY = 32;
    for (let i = 0; i < samples.length; i++) {
      if (i % ABORT_CHECK_EVERY === 0) input.signal?.throwIfAborted();
      const s = samples[i];
      muxer.addVideoChunkRaw(
        s.data,
        s.is_sync ? 'key' : 'delta',
        Math.round(s.cts * tsScale),
        Math.round(s.duration * tsScale) +
          (i === freeze.index ? freeze.extraUs : 0),
        i === 0 ? { decoderConfig } : undefined,
        Math.round((s.cts - s.dts) * tsScale)
      );
    }

    input.signal?.throwIfAborted();
    muxer.finalize();
    // close() awaits all pending writes per the spec, then closes the
    // stream. If any write failed asynchronously we'll have captured it.
    await writable.close();
    if (writeError) throw writeError;
    // Read the OPFS file back as a regular File (extends Blob). Browser
    // serves slices from disk on demand, so the upload pipeline can chunk
    // it without allocating multi-GB ArrayBuffers.
    const file = await fileHandle.getFile();
    invalidatePreviewAudio('after video export (blob)');
    return file;
    // NOTE: do NOT removeEntry(tempName) inline. The returned File still
    // references the OPFS-backed bytes; deleting the dir entry mid-upload
    // can invalidate slice() reads on some browsers. Cleanup happens via
    // sweepStaleMuxTempFiles() on next app load.
  } catch (err) {
    // On error: abort the stream + remove file inline (no live reference
    // exists since we never returned a File). Do NOT swallow original err.
    try { await writable.abort(); } catch { /* ignore */ }
    try { await opfsRoot.removeEntry(tempName); } catch { /* ignore */ }
    invalidatePreviewAudio('after video export (blob)');
    throw err;
  }
}

/** Sweep stale upload-mux temp files from OPFS. Called once at app startup
 *  by `src/main.tsx`. We never delete the temp file inline because the
 *  returned File still references it during the upload — browsers handle
 *  the live-reference-vs-removed-entry contract differently and we don't
 *  want to invalidate the upload mid-stream. Sweep on next session
 *  reclaims the disk quota cheaply. The `voicestudio-mux-` prefix is
 *  ours; sweep ignores any other files. */
export async function sweepStaleMuxTempFiles(): Promise<void> {
  if (!('storage' in navigator) || !navigator.storage?.getDirectory) return;
  try {
    const root = await navigator.storage.getDirectory();
    // @ts-expect-error — entries() is in the FileSystemDirectoryHandle spec
    // since 2023 but TS DOM lib hasn't caught up in our version.
    for await (const [name] of root.entries()) {
      if (typeof name === 'string' && name.startsWith('voicestudio-mux-')) {
        try {
          await root.removeEntry(name);
        } catch (e) {
          console.warn('[videoExport] OPFS sweep failed for', name, e);
        }
      }
    }
  } catch (e) {
    console.warn('[videoExport] OPFS sweep aborted:', e);
  }
}
